import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Utensils, ShoppingBasket, Package, Truck, Leaf, Building2, Plane, Ship, Train, Bike, ShieldCheck, MapPin, Briefcase, Zap, Coffee, Gift, Heart, Store, Wrench, Wine, Sparkles, Home, Baby, Book, Camera, Music } from 'lucide-react';
import Wordmark from './Wordmark';
import HeroSearch from './HeroSearch';
import CustomLandingSection from './CustomLandingSection';
import { publicSettings } from '../services/publicSettings';

const BRAND_GREEN = '#1E7D2B';
const BRAND_ORANGE = '#F1671B';

// Lookup for lucide icon names admin picks from — kept in sync with the
// icon dropdown in ConfigurationPage.jsx.
const ICON_MAP = {
  Utensils, ShoppingBasket, Package, Truck, Leaf, Building2, Plane, Ship,
  Train, Bike, ShieldCheck, MapPin, Briefcase, Zap, Coffee, Gift, Heart,
  Store, Wrench, Wine, Sparkles, Home, Baby, Book, Camera, Music,
};

const SERVICE_TILES = [
  { key: 'food', label: 'Food Delivery', icon: Utensils, color: BRAND_GREEN, cat: 'restaurants' },
  { key: 'african', label: 'African Grocery', icon: ShoppingBasket, color: '#7c3aed', cat: 'grocery', q: 'african' },
  { key: 'courier', label: 'Courier delivery', icon: Package, color: '#1e40af', cat: 'courier' },
  { key: 'same-day', label: 'Same-Day Package', icon: Truck, color: BRAND_ORANGE, cat: 'package' },
  { key: 'farm', label: 'Farm Produce', icon: Leaf, color: '#16a34a', cat: 'grocery', q: 'farm' },
  { key: 'business', label: 'Business Logistics', icon: Building2, color: '#0e7490', cat: 'logistics' },
];

const MODES = [
  { label: 'Road', icon: Truck },
  { label: 'Air', icon: Plane },
  { label: 'Sea', icon: Ship },
  { label: 'Rail', icon: Train },
  { label: 'Bike', icon: Bike },
];

const PROMISES = [
  { label: 'Fast & Reliable', icon: Zap },
  { label: 'Safe & Secure', icon: ShieldCheck },
  { label: 'Local & Nationwide', icon: MapPin },
  { label: 'For You & Your Business', icon: Briefcase },
];

const BrandHero = () => {
  const nav = useNavigate();
  const { t } = useTranslation();
  // Admin-editable landing content from platform_settings — falls back to i18n
  // defaults when the settings haven't been customised.
  const [cfg, setCfg] = useState({});
  useEffect(() => {
    publicSettings.get().then((s) => setCfg(s || {})).catch(() => {});
  }, []);
  const heroTitle = cfg.landing_hero_title || 'ShipitNow';
  const heroTagline = cfg.landing_hero_tagline || t('hero.tagline', 'Anything · Anytime · Delivered');
  const heroMission = cfg.landing_hero_mission || t('hero.mission', 'ShipItNow uses its registered and approved drivers to deliver food, same-day delivery, courier services, and business logistics in Canada and Ibadan.');
  const showTiles = cfg.landing_show_service_tiles !== false;
  const showModes = cfg.landing_show_modes_ribbon !== false;
  const showPromise = cfg.landing_show_promise_ribbon !== false;
  const sectionOrder = Array.isArray(cfg.landing_section_order) && cfg.landing_section_order.length
    ? cfg.landing_section_order
    : ['service_tiles', 'modes_ribbon', 'promise_ribbon'];
  const customSections = Array.isArray(cfg.landing_custom_sections) ? cfg.landing_custom_sections : [];
  const layoutById = cfg.landing_section_layout && typeof cfg.landing_section_layout === 'object' ? cfg.landing_section_layout : {};
  // Any custom sections whose ids aren't in sectionOrder append at the end so
  // freshly added ones still render even if the admin hasn't reordered yet.
  const knownIds = new Set(sectionOrder);
  const trailingCustomIds = customSections.map((s) => s.id).filter((id) => id && !knownIds.has(id));
  const effectiveOrder = [...sectionOrder, ...trailingCustomIds];
  const primaryColor = cfg.brand_primary_color || BRAND_GREEN;
  const accentColor = cfg.brand_accent_color || BRAND_ORANGE;
  const modesTitle = cfg.landing_modes_title || t('services.all_modes', 'All Modes · One Promise');
  const tileSize = cfg.landing_tile_size || 'default';
  const tileTuning = {
    compact: { padding: 'p-1.5 sm:p-2', icon: 'h-9 w-9 sm:h-10 sm:w-10', iconSvg: 'h-4 w-4 sm:h-5 sm:w-5', label: 'text-[10px] sm:text-[11px]', gap: 'gap-1.5 sm:gap-2' },
    default: { padding: 'p-2 sm:p-3', icon: 'h-10 w-10 sm:h-12 sm:w-12', iconSvg: 'h-5 w-5 sm:h-6 sm:w-6', label: 'text-[11px] sm:text-[12px]', gap: 'gap-2 sm:gap-2.5' },
    roomy: { padding: 'p-3 sm:p-4', icon: 'h-12 w-12 sm:h-14 sm:w-14', iconSvg: 'h-6 w-6 sm:h-7 sm:w-7', label: 'text-[12px] sm:text-[13px]', gap: 'gap-2.5 sm:gap-3' },
  }[tileSize] || undefined;
  const tile = tileTuning || {
    padding: 'p-2 sm:p-3', icon: 'h-10 w-10 sm:h-12 sm:w-12', iconSvg: 'h-5 w-5 sm:h-6 sm:w-6', label: 'text-[11px] sm:text-[12px]', gap: 'gap-2 sm:gap-2.5',
  };

  const SERVICE_TILES_I18N = [
    { key: 'food',     label: t('services.food', 'Food Delivery'),       icon: Utensils,       color: BRAND_GREEN,    cat: 'restaurants' },
    { key: 'african',  label: t('services.african', 'African Grocery'),  icon: ShoppingBasket, color: '#7c3aed',      cat: 'grocery', q: 'african' },
    { key: 'courier',  label: t('services.courier', 'Courier delivery'), icon: Package,        color: '#1e40af',      cat: 'courier' },
    { key: 'same-day', label: t('services.same_day', 'Same-Day Package'),icon: Truck,          color: BRAND_ORANGE,   cat: 'package' },
    { key: 'farm',     label: t('services.farm', 'Farm Produce'),        icon: Leaf,           color: '#16a34a',      cat: 'grocery', q: 'farm' },
    { key: 'business', label: t('services.business', 'Business Logistics'), icon: Building2,   color: '#0e7490',      cat: 'logistics' },
  ];
  const MODES_I18N = [
    { label: t('services.mode_road', 'Road'), icon: Truck },
    { label: t('services.mode_air', 'Air'),   icon: Plane },
    { label: t('services.mode_sea', 'Sea'),   icon: Ship },
    { label: t('services.mode_rail', 'Rail'), icon: Train },
    { label: t('services.mode_bike', 'Bike'), icon: Bike },
  ];
  const PROMISES_I18N = [
    { label: t('services.promise_fast', 'Fast & Reliable'), icon: Zap },
    { label: t('services.promise_safe', 'Safe & Secure'), icon: ShieldCheck },
    { label: t('services.promise_local', 'Local & Nationwide'), icon: MapPin },
    { label: t('services.promise_business', 'For You & Your Business'), icon: Briefcase },
  ];
  // Admin-editable service tiles. If landing_service_tiles is populated, use
  // that (excluding hidden entries). Otherwise fall back to the i18n defaults
  // so the home page never renders blank.
  const customTiles = Array.isArray(cfg.landing_service_tiles) ? cfg.landing_service_tiles.filter((x) => !x.hidden) : null;
  const tilesToRender = customTiles && customTiles.length > 0
    ? customTiles.map((c) => ({
        key: c.id,
        label: c.name,
        icon: ICON_MAP[c.icon] || Package,
        color: c.color || '#0EA5E9',
        cat: c.category || undefined,
        q: c.query || undefined,
      }))
    : SERVICE_TILES_I18N;
  return (
    <section
      data-testid="brand-hero"
      className="relative overflow-hidden rounded-2xl border border-slate-900 bg-slate-950 text-white shadow-xl"
    >
      {/* Decorative globe gradient + grain */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{ background: `radial-gradient(circle at 50% 35%, rgba(34,197,94,0.25), transparent 60%), radial-gradient(circle at 90% 80%, rgba(241,103,27,0.18), transparent 55%)` }} />
        <div className="absolute inset-0 bg-grain opacity-25" />
      </div>

      <div className="relative px-4 sm:px-10 pt-6 pb-5 sm:pt-10 sm:pb-8">
        {/* Wordmark + tagline */}
        <div className="flex flex-col items-center">
          <img
            src="/shipitnow-logo.png"
            alt="ShipitNow"
            className="hidden md:block max-w-[440px] w-full drop-shadow-[0_8px_24px_rgba(0,0,0,0.45)]"
            loading="eager"
            data-testid="hero-logo-desktop"
          />
          <div className="md:mt-3 flex flex-col items-center w-full">
            <Wordmark height={72} className="md:hidden" testid="hero-wordmark-mobile" />
            {heroTitle && heroTitle !== 'ShipitNow' && (
              <h1
                className="mt-2 text-center text-2xl sm:text-3xl font-black text-white tracking-tight drop-shadow"
                data-testid="hero-title"
              >
                {heroTitle}
              </h1>
            )}
            <p
              className="mt-2 sm:mt-1.5 text-center text-[13px] sm:text-base tracking-[0.18em] font-semibold uppercase hero-tagline-gradient"
              style={{
                background: `linear-gradient(90deg, ${primaryColor}, ${accentColor})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
              data-testid="hero-tagline"
            >
              {heroTagline}
            </p>
            <p
              className="mt-3 text-center text-[12px] sm:text-sm text-slate-200/90 max-w-2xl mx-auto leading-snug px-3"
              data-testid="hero-mission-statement"
            >
              {heroMission}
            </p>
          </div>
        </div>

        {/* Universal search — businesses, products, services */}
        <div className="mt-4 max-w-2xl mx-auto">
          <HeroSearch placeholder={cfg.landing_search_placeholder || t('hero.search_placeholder', 'Search businesses, products, services…')} />
        </div>

        {/* Landing sections — rendered in admin-configured order */}
        {effectiveOrder.map((id) => {
          const layout = layoutById[id];
          // Per-section layout style applied to the wrapper. Inline styles let
          // admins tune max-width / padding without a stylesheet round-trip.
          const layoutStyle = layout ? {
            maxWidth: layout.max_width_px ? `${layout.max_width_px}px` : undefined,
            marginLeft: layout.max_width_px ? 'auto' : undefined,
            marginRight: layout.max_width_px ? 'auto' : undefined,
            paddingTop: layout.padding_y != null ? `${layout.padding_y}px` : undefined,
            paddingBottom: layout.padding_y != null ? `${layout.padding_y}px` : undefined,
            paddingLeft: layout.padding_x != null ? `${layout.padding_x}px` : undefined,
            paddingRight: layout.padding_x != null ? `${layout.padding_x}px` : undefined,
          } : undefined;
          if (id === 'service_tiles' && showTiles) return (
            <div key="service_tiles" className={`mt-5 sm:mt-7 grid grid-cols-3 lg:grid-cols-6 ${tile.gap}`} style={layoutStyle} data-testid="brand-service-tiles">
              {tilesToRender.map((s) => {
                const Icon = s.icon;
                return (
                  <button
                    key={s.key}
                    onClick={() => {
                      const sp = new URLSearchParams();
                      if (s.cat) sp.set('cat', s.cat);
                      if (s.q) sp.set('q', s.q);
                      nav(`/?${sp.toString()}`);
                      setTimeout(() => {
                        const target = document.getElementById('home-vendor-list')
                                      || document.querySelector('[data-testid=home-vendor-list]');
                        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        else window.scrollTo({ top: 900, behavior: 'smooth' });
                      }, 80);
                    }}
                    data-testid={`service-tile-${s.key}`}
                    className={`group bg-white text-slate-900 rounded-xl ${tile.padding} text-center hover:bg-slate-50 transition-all hover:-translate-y-0.5 hover:shadow-lg`}
                  >
                    <div className={`${tile.icon} mx-auto rounded-lg flex items-center justify-center transition-transform group-hover:scale-110`}
                      style={{ backgroundColor: `${s.color}1A`, color: s.color }}>
                      <Icon className={tile.iconSvg} strokeWidth={2.2} />
                    </div>
                    <div className={`mt-1.5 sm:mt-2 ${tile.label} font-semibold leading-tight`}>{s.label}</div>
                  </button>
                );
              })}
            </div>
          );
          if (id === 'modes_ribbon' && showModes) return (
            <div key="modes_ribbon" className="mt-4 sm:mt-5 rounded-xl overflow-hidden border shadow-lg" style={{ borderColor: `${primaryColor}66`, ...(layoutStyle || {}) }} data-testid="how-it-works-video-section">
              <video
                src="/videos/how-it-works.mp4"
                poster="/videos/how-it-works-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                controls
                preload="metadata"
                className="w-full block bg-black"
                data-testid="how-it-works-video"
              />
            </div>
          );
          if (id === 'promise_ribbon' && showPromise) return (
            <div key="promise_ribbon" className="mt-3 rounded-xl text-white px-2 py-2 grid grid-cols-2 sm:grid-cols-4 gap-y-1.5 hero-promise-ribbon"
              style={{ background: `linear-gradient(90deg, ${primaryColor}, ${accentColor})`, ...(layoutStyle || {}) }}
              data-testid="brand-promise-ribbon"
            >
              {PROMISES_I18N.map((p) => {
                const Icon = p.icon;
                return (
                  <div key={p.label} className="flex items-center justify-center gap-1.5 text-[10px] sm:text-[11px] font-semibold tracking-wide">
                    <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" />
                    <span className="uppercase text-center leading-tight">{p.label}</span>
                  </div>
                );
              })}
            </div>
          );
          // Custom admin-defined sections
          const custom = customSections.find((s) => s.id === id);
          if (custom) return <CustomLandingSection key={id} section={custom} layout={layout} />;
          return null;
        })}
      </div>
    </section>
  );
};

export default BrandHero;
